import type { UiSettings } from "../storage.ts";
import type { LogEntry, LogLevel } from "../types.ts";
import type { LogsTailRequestFilters } from "./logs.ts";
import {
  entryMatchesLogsScope,
  sessionKeyFromPath,
  type SessionKeyPath,
} from "../../../../src/logging/log-scope.js";
import { parseSessionKeyPath } from "../chat/session-key-path.ts";

const ALL_LEVELS: LogLevel[] = ["trace", "debug", "info", "warn", "error", "fatal"];

export function resolveLogsScopePath(settings: UiSettings): SessionKeyPath {
  if (settings.logsScopeLinked) {
    return parseSessionKeyPath(settings.sessionKey);
  }
  return settings.logsScopePath;
}

export function buildLogsTailRequestFilters(
  settings: UiSettings,
  state: {
    logsFilterText: string;
    logsLevelFilters: Record<LogLevel, boolean>;
  },
): LogsTailRequestFilters {
  const enabledLevels = ALL_LEVELS.filter((level) => state.logsLevelFilters[level]);
  const base: LogsTailRequestFilters = {
    ...(enabledLevels.length > 0 && enabledLevels.length < ALL_LEVELS.length
      ? { levels: enabledLevels }
      : {}),
    ...(state.logsFilterText.trim() ? { needle: state.logsFilterText.trim() } : {}),
  };

  if (settings.logsViewAll) {
    return base;
  }

  const path = resolveLogsScopePath(settings);
  const sessionKeyPrefix = sessionKeyFromPath(path) ?? undefined;
  const hasPath = path.some((segment) => segment.trim().length > 0);

  return {
    ...base,
    ...(sessionKeyPrefix && hasPath ? { sessionKeyPrefix } : {}),
    ...(settings.logsAreaFilters.length > 0
      ? { subsystemPrefixes: [...settings.logsAreaFilters] }
      : {}),
    ...(sessionKeyPrefix && hasPath ? { includePlatform: settings.logsIncludePlatform } : {}),
  };
}

export function filterLogEntriesClient(
  entries: LogEntry[],
  settings: UiSettings,
  state: {
    logsFilterText: string;
    logsLevelFilters: Record<LogLevel, boolean>;
  },
): LogEntry[] {
  const path = resolveLogsScopePath(settings);
  const needle = state.logsFilterText.trim().toLowerCase();
  const areaFilters =
    settings.logsAreaFilters.length > 0 ? new Set(settings.logsAreaFilters) : null;

  return entries.filter((entry) => {
    if (entry.level && !state.logsLevelFilters[entry.level]) {
      return false;
    }
    if (needle) {
      const haystack = [entry.message, entry.subsystem, entry.raw, entry.sessionKey]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(needle)) {
        return false;
      }
    }
    if (settings.logsViewAll) {
      return true;
    }
    return entryMatchesLogsScope(entry as Parameters<typeof entryMatchesLogsScope>[0], {
      path,
      includePlatform: settings.logsIncludePlatform,
      areaFilters,
    });
  });
}

export function collectLogAreasFromEntries(entries: LogEntry[]): string[] {
  const areas = new Set<string>();
  for (const entry of entries) {
    const area = entry.area ?? entry.subsystem?.split("/")[0] ?? "";
    if (area) {
      areas.add(area.toLowerCase());
    }
  }
  return [...areas].sort((a, b) => a.localeCompare(b));
}
